/**
 * Hawker Profile service
 */
import {
  chunk,
  find,
  flatMap,
  get,
  has,
  head,
  isEmpty,
  isEqual,
  join,
  map,
  merge,
  omit,
  pick,
  reduce,
  set,
  sortBy,
  toLower,
  uniq,
  uniqBy,
} from 'lodash';
import { Injectable, Injector } from '@angular/core';

import { BaseService } from '../base';
import {
  IEntityConfig,
  ENTITY_TYPES,
  IApiListResponse,
  SiteType,
  IDocumentOperationOptions,
  IFilterQuery,
  IEmployee,
  GRID_ROW_SELECTION,
  BUTTON_FLEX_ORDER,
  ISite,
  IBaseEntityType,
  ICommissionRateFormAddition,
} from '@shared/types';
import { ColDef } from 'ag-grid-community';
import { I18n } from '@app/i18n/strings-keys';
import {
  BehaviorSubject,
  Observable,
  observableEMPTY,
  observableForkJoin,
  observableOf,
  rx,
} from '@app/shared/rx';
import { lazyInject } from '@app/containers/base/decorators';
import { XprtDataService } from '@app/shared/services/xprt-data/xprt-data.service';
import { BohDataService } from '../boh-data/boh-data.service';
import {
  ICommissionRate,
  IHawkerProfile,
  IHawkerProfileVirtual,
} from '@app/shared/types/entities/hawker-profile';
import { getObservableValue } from '@app/shared/utils/common-utils';
import { DEFAULT_PAGE_SIZE } from '@config/global-constants';
import { HttpParams } from '@angular/common/http';
import { XdmCommissionRateService } from '../xdm-commission-rate/xdm-commission-rate.service';
import { HawkerProfileColumnService } from './hawker-profile.column.service';
import { commissionRateRulesModalDefinition } from '../xvi-commission-rate/commission-rate-rules-modal.definition';
import { getCommissionRateAssignmentDefinition } from '../commission-rate-assignment/commission-rate-assignment.modal-definition';
import { BTN_CANCEL, BTN_YES } from '@app/text-content/button-templates';
import { XviCommissionRateService } from '../xvi-commission-rate';
import {
  batchRequestMap,
  defaultBatchMapAccumulatedPredict,
} from '@app/shared/rx-operators';

const entityType: ENTITY_TYPES = ENTITY_TYPES.HAWKER_PROFILE;
const config: IEntityConfig = {
  ENTITY_TYPE: entityType,
  isMasterListAvailable: true,
  entityNameToCheckPermissions: ENTITY_TYPES.HAWKER_CONFIG, // use hawker config permissions to control hawker profile access
  buttons: null,
  apiEndpoint: entityType,
  uiRoute: entityType,
  list: {
    documentQuery: {
      orderby: 'virtual.lastName asc',
    },
    isDefaultColumnsVisible: false,
    userGridOptions: {
      suppressRowClickSelection: true,
      enableServerSideSorting: false,
      enableServerSideFilter: false,
      rowSelection: GRID_ROW_SELECTION.SINGLE,
    },
    gridOptions: {
      valueField: '_id',
      showViewSelector: false,
      showSearch: true,
      showPagination: false,
      hiddenGridHeaderCheckbox: true,
    },
    isHiddenRefreshButton: true,
    useEntityNameForListTotal: true,
    columns: [],
  },
};

/**
 * Hawker service that extends BaseService to use
 * basic CRUD request operations
 */
@Injectable()
export class HawkerProfileService extends BaseService {
  @lazyInject(HawkerProfileColumnService)
  public hawkerProfileColumnService: HawkerProfileColumnService;

  @lazyInject(XprtDataService)
  public xprtDataService: XprtDataService;

  @lazyInject(XviCommissionRateService)
  public xviCommissionRateService: XviCommissionRateService;

  @lazyInject(XdmCommissionRateService)
  public xdmCommissionRateService: XdmCommissionRateService;

  @lazyInject(BohDataService)
  public bohDataService: BohDataService;

  public static config: IEntityConfig = config;
  public static ENTITY_TYPE: string = config.ENTITY_TYPE;

  public sites$: BehaviorSubject<ISite[]> = new BehaviorSubject<ISite[]>([]);

  public hawkerList$: BehaviorSubject<IHawkerProfile[]> = new BehaviorSubject<
    IHawkerProfile[]
  >([]);

  public quickFilterFields: string[] = ['name'];

  public listProjection: string[] = ['name'];

  public canModalSaveObs$: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);
  public handleAssignmentGridSave$: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);
  public isAssignmentGridChange$: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);

  public headerNames: ColDef[] = [
    {
      headerName: 'Name',
      field: 'name',
      filter: false,
    },
  ];

  public constructor(public injector: Injector) {
    super(injector);
    this.config = HawkerProfileService.config;

    this.config.buttons = {
      ...(this.config.buttons ?? {}),
      assign_rate: {
        flexOrder: BUTTON_FLEX_ORDER.LEFTMOST,
        isFlattenButtonToActionPanel: true,
        caption: I18n.Forms.HawkerProfileForm.AssignCommissionRate,
        hidden: (selectedItems: IHawkerProfile[]): boolean => {
          return !isEqual(selectedItems.length, 1);
        },
        click: this.handleAssignRateClick.bind(this),
      },
    };

    set(
      this.config,
      'list.columns',
      this.hawkerProfileColumnService.buildColumns({
        sites$: this.sites$,
        hawkerList$: this.hawkerList$,
        handleAssignRateClick: this.handleAssignRateClick.bind(this),
      }),
    );
  }

  public fetchList(): Observable<IApiListResponse> {
    return this.fetchHawkerList().pipe(
      rx.map((hawkerList) => {
        return {
          items: hawkerList,
          total: hawkerList.length,
          current_page: 1,
          page_count: 1,
          page_size: hawkerList.length,
        };
      }),
      // effect: update hawkerList$
      rx.tap((hawkerListRes) => {
        // resort and uniq sites
        const allSitesInfo = this.sortSitesByName(
          uniqBy(flatMap(hawkerListRes.items, 'virtual.sites'), 'id'),
        );

        // update sites$
        this.sites$.next(allSitesInfo);

        this.hawkerList$.next(hawkerListRes.items as IHawkerProfile[]);
      }),
    );
  }

  /**
   *
   * override this to provide custom getDocumentById logic
   * so we can fetch data with employee details
   *
   * @param employeeId
   * @returns
   */
  public getDocumentById(employeeId: string): Observable<any> {
    const employeeInfosObs = this.bohDataService.getEmployeeByIds([employeeId]);
    const employeeBasicInfosObs = this.fetchBasicEmployeeInfo({ employeeId });

    return observableForkJoin([employeeInfosObs, employeeBasicInfosObs]).pipe(
      rx.map(([employeeInfosRes, employeeBasicInfosRes]) =>
        this.formatDocument(
          get(employeeInfosRes, 'items.[0]'),
          head(employeeBasicInfosRes),
        ),
      ),
    );
  }

  /**
   *
   * use this to get combined employee info with hawker profile & xdm commission rate
   *
   * @param employeeBasicInfos base on employee_id to get more info
   * @returns
   */
  public combineEmployeeInfo(employeeBasicInfos: Partial<IHawkerProfile>[]) {
    const employeeIds = map(employeeBasicInfos, 'employee_id');

    const employeeInfosObs = this.bohDataService.getEmployeeByIds(employeeIds);
    const hawkerProfilesObs = this.fetchHawkerProfileList({ employeeIds });

    return observableForkJoin([employeeInfosObs, hawkerProfilesObs]).pipe(
      rx.map(([employInfoRes, hawkerProfilesRes]) => {
        const employeeInfos = employInfoRes?.items;
        const hawkerProfiles = hawkerProfilesRes?.items;

        return map(employeeInfos, (employeeInfo) => {
          const employeeBasicInfo = find(employeeBasicInfos, {
            employee_id: employeeInfo.EmployeeId,
          });

          const hawkerProfile = find(hawkerProfiles, {
            employee_id: employeeBasicInfo?.employee_id,
          });

          return this.formatDocument(
            employeeInfo,
            employeeBasicInfo,
            hawkerProfile,
          );
        });
      }),
      rx.catchError(() => observableEMPTY),
    );
  }

  public fetchBasicEmployeeInfo(options?: {
    employeeId?: string;
  }): Observable<Partial<IHawkerProfile>[]> {
    return this.xprtDataService
      .getAllSitesByFilter({
        filter_by_assigned_sites: false,
        filter: `business_type eq in(${SiteType.VendingCommissary})`,
        select: ['id', 'name', 'store_number'],
      })
      .pipe(
        rx.mergeMap((sites) => {
          const allSiteIds = map(sites, 'id');

          return this.bohDataService
            .getEmployeeSiteStatus(
              { siteIds: allSiteIds, employeeId: options?.employeeId },
              { removeDuplicate: true },
            )
            .pipe(
              rx.map((employees) => {
                if (isEmpty(employees) || isEmpty(employees?.items)) {
                  return [];
                }

                const employeeSiteMap = reduce(
                  employees?.items,
                  (
                    employeeBasicInfos: Partial<IHawkerProfile>[],
                    curEmployee,
                  ) => {
                    const siteInfo = sites.find(
                      (site) => site.id === curEmployee.SiteId,
                    );

                    if (!siteInfo) {
                      return employeeBasicInfos;
                    }

                    if (siteInfo) {
                      const existEmployee = find(employeeBasicInfos, {
                        employee_id: curEmployee.EmployeeId,
                      });

                      if (!isEmpty(existEmployee)) {
                        if (
                          isEmpty(
                            find(existEmployee.virtual?.sites, {
                              id: siteInfo.id,
                            }),
                          )
                        ) {
                          existEmployee.virtual.sites = [
                            ...existEmployee.virtual.sites,
                            siteInfo,
                          ];
                        }
                      } else {
                        const newEmployee: Partial<IHawkerProfile> = {
                          _id: curEmployee.EmployeeId,
                          employee_id: curEmployee.EmployeeId,
                          virtual: {
                            sites: [siteInfo],
                          },
                        };

                        employeeBasicInfos.push(newEmployee);
                      }
                    }

                    return employeeBasicInfos;
                  },
                  [],
                );

                return employeeSiteMap;
              }),
            );
        }),
      );
  }

  /**
   * Fetch hawker list
   * if your want to filter by employee id, pass in options.employeeId
   *
   * @param options
   * @returns
   */
  public fetchHawkerList(options?: {
    employeeId?: string;
  }): Observable<IHawkerProfile[]> {
    return this.fetchBasicEmployeeInfo(options).pipe(
      rx.switchMap((employeeBasicInfos) => {
        const emptyResult = [];

        if (isEmpty(employeeBasicInfos)) {
          return observableOf(emptyResult);
        }

        // get employee info & commission rate
        return this.combineEmployeeInfo(employeeBasicInfos);
      }),
    ) as any;
  }

  public combineDmCommissionRates(commissionRates: ICommissionRate[]) {
    const commissionRateIds = uniq(map(commissionRates, 'entity_id'));

    if (isEmpty(commissionRateIds)) {
      return observableOf(commissionRates);
    }

    return this.xdmCommissionRateService
      .fetchList({
        filter: `entity_id eq in(${join(commissionRateIds, ',')})`,
        select: ['name', 'entity_id', '_id'],
      })
      .pipe(
        rx.map((dmCommissionRatesRes) => {
          const dmCommissionRates = dmCommissionRatesRes.items;

          if (isEmpty(dmCommissionRates)) {
            return commissionRates;
          }

          return map(commissionRates, (commissionRate) => ({
            ...commissionRate,
            name: find(dmCommissionRates, {
              entity_id: commissionRate.entity_id,
            })?.name,
          }));
        }),
      );
  }

  /**
   * Fetch hawker profile list of xvi
   *
   * @param userQuery
   * @param employeeIds
   * @param options
   * @returns
   */
  public fetchHawkerProfileList(
    userQuery?: IFilterQuery & {
      /**
       * this will override userQuery filter for batch employee_id
       */
      employeeIds?: string[];
    },
    options: IDocumentOperationOptions = {},
  ): Observable<IApiListResponse> {
    const headers = this.composeHeadersForUpdateRequest({
      ...options,
      disallowBusinessDateHeaders: true,
    });
    const query: IFilterQuery = merge(
      {},
      this.defaultQuery,
      omit(userQuery, 'employeeIds'),
    );

    const employeeIds = userQuery?.employeeIds ?? [];

    const requestHawkerProfiles = (chunkEmployeeIds?: string[]) => {
      const defaultUserQuery: IFilterQuery = {
        select: ['employee_id', 'commission_rates'],
      };

      const createParamsQuery = isEmpty(chunkEmployeeIds)
        ? {
            ...defaultUserQuery,
            ...query,
          }
        : {
            ...defaultUserQuery,
            ...query,
            filter: `employee_id eq in(${join(chunkEmployeeIds, ',')})`,
          };
      const params: HttpParams = this.createParams(createParamsQuery);

      return this.http
        .get<IApiListResponse>(this.getListBaseUrl(), {
          params,
          headers,
        })
        .pipe(
          this.catchHttpError(
            options.errorOverride,
            options.showErrorMessage,
            undefined,
            { ...pick(options, ['notCheckPermissionError', 'throwError']) },
          ),
        );
    };

    const requestObs: Observable<IApiListResponse> = isEmpty(employeeIds)
      ? requestHawkerProfiles()
      : batchRequestMap(
          chunk(employeeIds, DEFAULT_PAGE_SIZE),
          requestHawkerProfiles,
        ).pipe(rx.reduce(defaultBatchMapAccumulatedPredict, {} as any));

    return requestObs.pipe(
      rx.mergeMap((hawkerProfilesRes) => {
        if (isEmpty(hawkerProfilesRes.items)) {
          return observableOf(hawkerProfilesRes);
        }

        const allCommissionRates = flatMap(
          hawkerProfilesRes.items,
          (hawkerProfiles) => hawkerProfiles.commission_rates,
        );

        return this.combineDmCommissionRates(allCommissionRates).pipe(
          rx.map((combinedCommissionRates) => {
            if (isEmpty(combinedCommissionRates)) {
              return hawkerProfilesRes;
            }

            return {
              ...hawkerProfilesRes,
              items: map(hawkerProfilesRes.items, (hawkerProfile) => {
                return {
                  ...hawkerProfile,
                  commission_rates: map(
                    hawkerProfile.commission_rates,
                    (commissionRate) => {
                      const dmCommissionRate = find(combinedCommissionRates, {
                        entity_id: commissionRate.entity_id,
                      });

                      return {
                        ...commissionRate,
                        name: dmCommissionRate?.name,
                      };
                    },
                  ),
                };
              }),
            };
          }),
          rx.catchError(() => {
            return observableOf(hawkerProfilesRes);
          }),
        );
      }),
      rx.map((res) => this.backToFrontMapper(res, options)),
    );
  }

  /**
   * Handle assign rate button click
   * Opens commission rate assignment modal and refreshes data after successful save
   *
   * @param selectedItems - Selected hawker profiles (must be single selection)
   */
  public async handleAssignRateClick(
    selectedItems: IHawkerProfile[],
  ): Promise<void> {
    const employeeId = get(head(selectedItems), 'employee_id', '');
    const employeeFullName = this.getEmployeeFullName(head(selectedItems));

    if (!employeeId) {
      return;
    }

    const formAddition: ICommissionRateFormAddition = {
      employeeId,
      canSaveObs: this.canModalSaveObs$,
      handleAssignmentGridSaveObs: this.handleAssignmentGridSave$,
      isAssignmentGridChangeObs: this.isAssignmentGridChange$,
    };
    const formEventHandler = {
      beforeFormClose: (confirm: boolean) =>
        this.handleAssignmentModalClose(confirm),
    };
    const modalDefinition = getCommissionRateAssignmentDefinition(
      {
        formAddition,
        title: `${this.i18nService.translate(I18n.Forms.HawkerProfileForm.CommissionRateAssignment)} - ${employeeFullName}`,
      },
      formEventHandler,
    );

    const formModal = await this.uiActions.formModalDialog(modalDefinition);
    if (!formModal?.confirmed) {
      this.canModalSaveObs$.next(false);

      return;
    }

    this.handleAssignmentGridSave$.next(true);
    this.canModalSaveObs$.next(false);

    await this.refreshHawkerListAndSites();
  }

  /**
   * Refresh hawker list and sites data after commission rate updates
   *
   * @returns Promise that resolves when refresh is complete
   */
  protected async refreshHawkerListAndSites(): Promise<void> {
    try {
      // Fetch updated data from server
      const hawkerListRes = await this.fetchList().toPromise();

      // Update hawkerList$ to trigger commission rate filter refresh
      this.hawkerList$.next(hawkerListRes.items as IHawkerProfile[]);

      // Extract and sort unique sites from updated hawker list
      const allSitesInfo = this.sortSitesByName(
        uniqBy(flatMap(hawkerListRes.items, 'virtual.sites'), 'id'),
      );

      // Update sites$ to ensure site filters remain in sync
      // This is necessary because:
      // 1. Site assignments may change when commission rates are updated
      // 2. Maintains consistency with fetchList() behavior
      // 3. Ensures all filters reflect the latest data state
      this.sites$.next(allSitesInfo);
    } catch (error) {}
  }

  protected sortSitesByName(sites: ISite[]): ISite[] {
    return sortBy(sites, (site) => toLower(site.name));
  }

  /**
   * Get employee full name
   *
   * @param hawkerProfile
   * @return employee full name
   */
  public getEmployeeFullName(hawkerProfile: Partial<IHawkerProfile>): string {
    const virtual = get(hawkerProfile, 'virtual');

    return `${virtual?.firstName || ''} ${virtual?.lastName || ''}`
      .replace(/\s+/g, ' ')
      .trim();
  }

  public formatDocument(
    employeeInfo: IEmployee,
    employeeBasicInfo?: Partial<IHawkerProfile>,
    hawkerProfile?: Partial<IHawkerProfile>,
  ): Partial<IHawkerProfile> {
    const sortedSites = this.sortSitesByName(employeeBasicInfo?.virtual?.sites);

    const virtualEmployeeInfo: IHawkerProfileVirtual = {
      firstName: employeeInfo?.Firstname || '',
      lastName: employeeInfo?.Lastname || '',
      badgeNumber: employeeInfo?.BadgeNumber || '',
      employeeCorporateCode: employeeInfo?.EmployeeCorporateCode || '',
      sites: sortedSites || [],
      formattedSites: map(sortedSites, (site) => ({
        items: [
          {
            item: site.name,
            itemClass: 'site-name-item',
          },
          {
            item: `[${site.store_number}]`,
            itemClass: 'store-number-item',
          },
        ],
      })),
    };

    return {
      ...(employeeBasicInfo ?? {}),
      entity_id: get(
        hawkerProfile,
        'employee_id',
        employeeBasicInfo?.employee_id,
      ),
      virtual: {
        ...(employeeBasicInfo?.virtual ?? {}),
        ...(virtualEmployeeInfo ?? {}),
      },
      commission_rates: hawkerProfile?.commission_rates || [],
    };
  }

  public async openCommissionRateRuleModal(
    options: {
      commissionRateId: string;
      excludeCommissionRateIds: string[];
      rateName?: string;
    } = {
      commissionRateId: '',
      excludeCommissionRateIds: [],
      rateName: '',
    },
  ): Promise<void> {
    const def = commissionRateRulesModalDefinition.commissionRateRulesModal(
      this.xviCommissionRateService,
      { ...options },
    );
    await this.uiActions.promptFormModal(def);

    return;
  }

  public frontToBackItemMapper(doc: IBaseEntityType): IBaseEntityType {
    const newDoc = { ...doc };

    if (has(newDoc, 'commission_rates')) {
      newDoc.commission_rates = map(newDoc.commission_rates, (rate) =>
        pick(rate, ['entity_id', 'is_primary']),
      );
    }

    return super.frontToBackItemMapper(newDoc);
  }

  public updateEmployeeCommissionRate(
    documentId: string,
    commissionRate: ICommissionRate[],
  ): Observable<IBaseEntityType> {
    const handledBodyRate = this.prepareDataForRequest(
      { commission_rates: commissionRate },
      {},
    ) as {
      commission_rates: ICommissionRate[];
    };

    return this.updateEntity(handledBodyRate, {
      endpoint: documentId,
    });
  }

  public saveEmployeeCommissionRate(
    employeeId: string,
    commissionRate: ICommissionRate[],
  ): Observable<IBaseEntityType> {
    const handledBodyRate = this.prepareDataForRequest(
      { employee_id: employeeId, commission_rates: commissionRate },
      {},
    ) as {
      commission_rates: ICommissionRate[];
    };

    return this.createEntity(handledBodyRate);
  }

  public async handleAssignmentModalClose(confirm: boolean): Promise<boolean> {
    if (confirm) {
      return true;
    }

    const isGridChange = getObservableValue(this.isAssignmentGridChange$);
    if (isGridChange) {
      const leaveConfirmMsg = {
        text: I18n.InfoMessages.AreYouSureYouWantToDiscardAllChanges,
        buttons: [BTN_YES, BTN_CANCEL],
      };
      const confirmInfo = await this.uiActions.confirm({
        ...leaveConfirmMsg,
      });
      if (confirmInfo.confirmed) {
        this.isAssignmentGridChange$.next(false);

        return true;
      }
    } else {
      return true;
    }
  }
}
